/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import {
    DetailsList,
    IColumn,
    IDetailsListStyles,
    IDragDropEvents,
    SelectionMode,
} from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { Text } from '@fluentui/react/lib/Text';
import { TextField } from '@fluentui/react/lib/TextField';
import { makeStyles, Theme } from '@fluentui/react/lib/Theme';
import { useBoolean } from '@uifabric/react-hooks';
import * as React from 'react';

import { DynamicRouteType, FallbackPolicyType, RouteTypes } from '../../../../../../service/gen/templates';
import {
    UI_DynamicRouteInput,
    UI_FallbackPolicyInput,
    UI_StaticRouteInput,
} from '../../../../../../utilities/TemplateUtils';
import { DataSourceType } from '../../../../TemplatePeoplePicker/TemplatePeoplePicker.container';
import { TemplateInstanceApproverSettingsV2 } from './TemplateApproverSettingsV2';

export interface UI_StepRouteSettingInput {
    order: number;
    approverRouteType: RouteTypes;
    staticRoute?: UI_StaticRouteInput;
    dynamicRoute?: UI_DynamicRouteInput;
    fallbackPolicy?: UI_FallbackPolicyInput;
    error?: string;
}

export interface TemplateSequentialApproverSettingsItemsProps {
    items: UI_StepRouteSettingInput[];
    dataSourceType?: DataSourceType;
    dataSourceTeamId?: string;
    isAddRecipientDisabled?: boolean;
    completeInOrder?: boolean;
    required?: boolean;
    addButtonText: string;
    dragHandleAriaLabel?: string;
    orderAriaLabel: string;
    removeAriaLabel: string;
    emailAriaLabel: string;
    itemLimit?: number;
    gridLabel?: string;
    enableEmail?: boolean;
    onUpdate: (
        callback:
            | UI_StepRouteSettingInput[]
            | ((stepSettings: UI_StepRouteSettingInput[]) => UI_StepRouteSettingInput[])
    ) => void;
    header?: string;
}

function move<T>(arr: T[], element: T, to: number): T[] {
    const result = arr.filter((a: T): boolean => a !== element);
    result.splice(to, 0, element);
    return result;
}

function updateOrder(recipients: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] {
    return recipients.map(
        (r: UI_StepRouteSettingInput, index: number): UI_StepRouteSettingInput =>
            r.order === index + 1 ? r : { ...r, order: index + 1 }
    );
}
const validateOrder = (order: string): boolean => /^[0-9]*$/.test(order);

const useDetailsListStyles = makeStyles((theme: Theme): Partial<IDetailsListStyles> => {
    return {
        root: {
            paddingTop: '4px',
            '.drag-handle-cell': {
                cursor: 'move',
                userSelect: 'none',
            },
            '.template-settings.ms-DetailsRow-cell': {
                display: 'block',
            },
        },
        contentWrapper: {
            '.template-settings .ms-DetailsRow-cell': {
                display: 'block',
            },
            '.drop-target': {
                boxShadow: `0 -1px 0 ${theme.palette.black}`,
            },
        },
    };
});

export const TemplateSequentialApproverSettings: React.FC<TemplateSequentialApproverSettingsItemsProps> = ({
    items,
    isAddRecipientDisabled,
    // completeInOrder,
    addButtonText,
    dragHandleAriaLabel,
    orderAriaLabel,
    removeAriaLabel,
    emailAriaLabel,
    gridLabel,
    itemLimit = 10,
    dataSourceTeamId,
    dataSourceType,
    header,
    onUpdate,
}: React.PropsWithChildren<TemplateSequentialApproverSettingsItemsProps>): JSX.Element => {
    const [draggable, { setTrue: setDraggableTrue, setFalse: setDraggableFalse }] = useBoolean(false);

    const hasDirectManager = !!items.find(
        (v: UI_StepRouteSettingInput): boolean => v.approverRouteType === RouteTypes.DynamicRoute
    );
    // Render drag handle for moving a recipient
    const onRenderDragHandle = (): JSX.Element => (
        <Icon
            iconName='GripperDotsVertical'
            aria-label={dragHandleAriaLabel}
            className='drag-handle'
            onMouseDown={setDraggableTrue}
            onMouseLeave={setDraggableFalse}
        />
    );

    // Render recipient order input
    const onRenderOrder = (item: UI_StepRouteSettingInput): JSX.Element => {
        // On order changes, update the order only if it's a valid number
        const onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
            const newValue = evt.target.value;
            if (validateOrder(newValue)) {
                const order = newValue && parseInt(newValue, 10);
                onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
                    items.map(
                        (v: UI_StepRouteSettingInput): UI_StepRouteSettingInput => (v === item ? { ...item, order } : v)
                    )
                );
            }
        };

        // On order blur, move the recipient accordingly
        const onBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
            const newValue = evt.target.value;
            if (validateOrder(newValue)) {
                onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] => {
                    const newRecipients = [...items].sort(
                        (a: UI_StepRouteSettingInput, b: UI_StepRouteSettingInput): number => a.order - b.order
                    );
                    return updateOrder(newRecipients);
                });
            }
        };

        return (
            <TextField
                className='order-input'
                ariaLabel={orderAriaLabel}
                value={item.order as unknown as string}
                type='text'
                maxLength={2}
                onChange={onChange}
                onBlur={onBlur}
            />
        );
    };

    // On render step settings
    const onRenderSettings = (item: UI_StepRouteSettingInput): JSX.Element => {
        return (
            <div className={mergeStyles('email-input', item.error && 'is-error')}>
                <TemplateInstanceApproverSettingsV2
                    inline
                    hideLabel
                    allowDirectManager={
                        (!hasDirectManager || item.approverRouteType === RouteTypes.DynamicRoute) && !!!dataSourceTeamId
                    }
                    allowTeamTag={false}
                    approverRouteType={item.approverRouteType}
                    dataSourceType={dataSourceType}
                    dataSourceTeamId={dataSourceTeamId}
                    dynamicRoute={item.dynamicRoute}
                    staticRoute={item.staticRoute}
                    fallbackPolicy={item.fallbackPolicy}
                    onApproverRouteTypeChange={(routeType: RouteTypes): void => {
                        const dynamicRoute = {
                            AllMustRespond: false,
                            Items: [
                                {
                                    Type: DynamicRouteType.DirectManager,
                                },
                            ],
                        };
                        onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
                            items.map(
                                (v: UI_StepRouteSettingInput): UI_StepRouteSettingInput =>
                                    v.order === item.order
                                        ? {
                                              ...v,
                                              approverRouteType: routeType,
                                              ...(routeType === RouteTypes.DynamicRoute ? { dynamicRoute } : {}),
                                          }
                                        : v
                            )
                        );
                    }}
                    onFallbackPolicyChange={(fallbackPolicy: UI_FallbackPolicyInput): void => {
                        if (fallbackPolicy) {
                            fallbackPolicy.FallbackApproversAllMustRespond = false;
                        }
                        onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
                            items.map(
                                (v: UI_StepRouteSettingInput): UI_StepRouteSettingInput =>
                                    v.order === item.order ? { ...v, fallbackPolicy } : v
                            )
                        );
                    }}
                    onStaticRouteChange={(staticRoute: UI_StaticRouteInput): void => {
                        if (staticRoute) {
                            staticRoute.AllMustRespond = false;
                        }
                        onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
                            items.map(
                                (v: UI_StepRouteSettingInput): UI_StepRouteSettingInput =>
                                    v.order === item.order ? { ...v, staticRoute } : v
                            )
                        );
                    }}
                    onDynamicRouteChange={(dynamicRoute: UI_DynamicRouteInput): void => {
                        if (dynamicRoute) {
                            dynamicRoute.AllMustRespond = false;
                        }
                        onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
                            items.map(
                                (v: UI_StepRouteSettingInput): UI_StepRouteSettingInput =>
                                    v.order === item.order ? { ...v, dynamicRoute } : v
                            )
                        );
                    }}
                />
                {item.error && (
                    <Text className='error-message' role='alert'>
                        {item.error}
                    </Text>
                )}
            </div>
        );
    };

    // On render remove button
    const onRenderRemoveButton = (item: UI_StepRouteSettingInput): JSX.Element => {
        const onClick = (): void => {
            onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
                updateOrder(items.filter((v: UI_StepRouteSettingInput): boolean => v !== item))
            );
        };

        return (
            <IconButton
                className='remove-button'
                iconProps={{
                    iconName: 'Cancel',
                }}
                disabled={items.length <= 1}
                ariaLabel={removeAriaLabel}
                onClick={onClick}
            />
        );
    };

    const columns: IColumn[] = [
        {
            key: 'email',
            name: emailAriaLabel,
            minWidth: 100,
            fieldName: 'email',
            className: 'approver-cell template-settings',
            onRender: onRenderSettings,
        },
        {
            key: 'remove',
            name: null,
            minWidth: 28,
            maxWidth: 28,
            className: 'remove-cell template-settings',
            onRender: onRenderRemoveButton,
        },
    ];

    // Insert drag handle and order column only if completeInOrder is enabled
    // if (completeInOrder) {
    columns.splice(
        0,
        0,
        {
            key: 'dragHandle',
            name: null,
            minWidth: 14,
            maxWidth: 14,
            className: 'drag-handle-cell template-settings',
            onRender: onRenderDragHandle,
        },
        {
            key: 'order',
            name: null,
            minWidth: 32,
            maxWidth: 32,
            className: 'template-settings',
            onRender: onRenderOrder,
        }
    );
    // }

    // Details list properties for drag and drop events
    const dragDropEvents = React.useMemo((): IDragDropEvents => {
        let draggedItem: UI_StepRouteSettingInput;
        const moveRecipient = (recipient: UI_StepRouteSettingInput, toRecipient: UI_StepRouteSettingInput): void => {
            if (recipient !== toRecipient) {
                onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] => {
                    const recipients = [...items];
                    const toIndex = recipients.indexOf(toRecipient);
                    if (toIndex !== -1) {
                        const newRecipients = move(recipients, recipient, toIndex);
                        return updateOrder(newRecipients);
                    }
                    return recipients;
                });
            }
        };
        return {
            canDrop: (): boolean => draggable,
            canDrag: (): boolean => draggable,
            onDragEnter: (): string => 'drop-target',
            onDrop: (item: UI_StepRouteSettingInput): void => {
                if (draggedItem) {
                    moveRecipient(draggedItem, item);
                }
            },
            onDragStart: (item: UI_StepRouteSettingInput): void => {
                draggedItem = item;
            },
            onDragEnd: (): void => {
                draggedItem = null;
            },
        };
    }, [draggable, onUpdate]);

    // Disable virtualization of the list
    const onShouldVirtualize = (): boolean => false;

    const onClick = (): void => {
        onUpdate((items: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] =>
            items.concat({
                order: items.length + 1,
                approverRouteType: RouteTypes.LetUserEnter,
                staticRoute: {
                    Approvers: [],
                },
                dynamicRoute: {
                    Items: [],
                    AllMustRespond: false,
                },
                fallbackPolicy: {
                    Type: FallbackPolicyType.LetUserEnter,
                    FallbackApprovers: [],
                    FallbackApproversAllMustRespond: false,
                },
            })
        );
    };

    const detailsListStyles = useDetailsListStyles();
    return (
        <>
            <Text>{header}</Text>
            {items && items.length > 0 && (
                <DetailsList
                    className='recipient-list'
                    ariaLabelForGrid={gridLabel}
                    columns={columns}
                    items={items}
                    selectionMode={SelectionMode.none}
                    dragDropEvents={dragDropEvents}
                    isHeaderVisible={false}
                    onColumnHeaderClick={null}
                    onShouldVirtualize={onShouldVirtualize}
                    styles={detailsListStyles}
                />
            )}
            <div>
                <ActionButton
                    disabled={isAddRecipientDisabled || itemLimit <= items.length}
                    onClick={onClick}
                    iconProps={{ iconName: 'AddFriend' }}>
                    {addButtonText}
                </ActionButton>
            </div>
        </>
    );
};
